import { type QueryClient, queryOptions, useQuery } from "@tanstack/react-query"
import { friendsQueryOptions } from "@/entities/friend"
import { scenarioKeys } from "../model/keys"
import type { PendingScenario } from "../model/types"
import { getPendingScenarios, getScenario } from "./client"

export function scenarioQueryOptions(friendId: number) {
	return queryOptions({
		queryKey: scenarioKeys.current(friendId),
		queryFn: () => getScenario(friendId),
		staleTime: Number.POSITIVE_INFINITY,
	})
}

export function useScenario(friendId: number, options?: { enabled?: boolean }) {
	return useQuery({
		...scenarioQueryOptions(friendId),
		enabled: options?.enabled ?? true,
	})
}

// Catch-up delivery on app start / WS reconnect: mark every cached scenario
// state stale (no forced refetch — the bulk response below refreshes the
// active ones, the rest refetch on demand), then seed both halves of each
// popup — the friend's proposal direction and the scenario text — so a missed
// WS event cannot leave a chat unblocked or a popup empty.
export async function primePendingScenarios(
	queryClient: QueryClient,
	userId: number,
): Promise<void> {
	queryClient.invalidateQueries({
		queryKey: scenarioKeys.all,
		refetchType: "none",
	})
	try {
		const pending = await getPendingScenarios()
		const proposals = new Map<number, PendingScenario["proposal"]>()

		for (const item of pending) {
			const { queryKey } = scenarioQueryOptions(item.friend_id)
			queryClient.setQueryData(queryKey, item)
			proposals.set(item.friend_id, item.proposal)
		}

		// One pass over the friend list: updateFriendInCache clones the whole
		// array per call, and this runs on every reconnect.
		const { queryKey: friendsKey } = friendsQueryOptions(userId)
		queryClient.setQueryData(friendsKey, (old) =>
			old?.map((friend) => ({
				...friend,
				scenarios: {
					...friend.scenarios,
					proposal: proposals.get(friend.id) ?? "none",
				},
			})),
		)
		queryClient.invalidateQueries({
			queryKey: friendsKey,
			refetchType: "none",
		})
	} catch {
		// A failed prime is safe: popups fetch the state on demand.
	}
}
